import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import {
  usePricingScenarios,
  toPricingScenarioSelectOptions,
} from '../../../graphql/queries/settlement/getSettlementCommonQueries';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import {
  GET_PRICING_FEE_ITEM_PAGE,
  CREATE_PRICING_FEE_ITEM,
  UPDATE_PRICING_FEE_ITEM,
} from '../../../graphql/queries/settlement/pricingFeeItemQueries';

interface PricingFeeItemData {
  id: string;
  businessFeeCode: number;
  businessFeeName: string;
  businessScenarioCode: number;
  businessCode: number;
  description?: string | null;
}

interface PricingFeeItemPageResponse {
  content: PricingFeeItemData[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const PAGE_SIZE = 20;

const PricingFeeItemTab: React.FC = () => {
  const [pendingId, setPendingId] = useState('');
  const [pendingCode, setPendingCode] = useState('');
  const [pendingName, setPendingName] = useState('');
  const [pendingScenarioCode, setPendingScenarioCode] = useState<number | undefined>();

  const [appliedId, setAppliedId] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [appliedName, setAppliedName] = useState('');
  const [appliedScenarioCode, setAppliedScenarioCode] = useState<number | undefined>();
  const [currentPage, setCurrentPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PricingFeeItemData | null>(null);
  const [form] = Form.useForm();

  const filters = useMemo(() => {
    const f: Record<string, any> = {};
    if (appliedId) f.id = Number(appliedId);
    if (appliedCode) f.businessFeeCode = Number(appliedCode);
    if (appliedName) f.businessFeeNameLike = appliedName;
    if (appliedScenarioCode !== undefined) f.businessScenarioCode = appliedScenarioCode;
    return f;
  }, [appliedId, appliedCode, appliedName, appliedScenarioCode]);

  const { data, loading, refetch } = useQuery<{ getPricingFeeItemPage: PricingFeeItemPageResponse }>(
    GET_PRICING_FEE_ITEM_PAGE,
    {
      variables: {
        filters: Object.keys(filters).length > 0 ? filters : null,
        pageRequest: { page: currentPage, size: PAGE_SIZE },
      },
      fetchPolicy: 'network-only',
    }
  );

  const [createFeeItem, { loading: creating }] = useMutation(CREATE_PRICING_FEE_ITEM);
  const [updateFeeItem, { loading: updating }] = useMutation(UPDATE_PRICING_FEE_ITEM);

  const { scenarios } = usePricingScenarios();
  const scenarioOptions = toPricingScenarioSelectOptions(scenarios);
  const scenarioMap = useMemo(
    () => new Map(scenarios.map(s => [s.businessScenarioCode, s.businessScenarioName])),
    [scenarios]
  );

  const pageData = data?.getPricingFeeItemPage;

  const handleSearch = () => {
    setAppliedId(pendingId);
    setAppliedCode(pendingCode);
    setAppliedName(pendingName);
    setAppliedScenarioCode(pendingScenarioCode);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setPendingId('');
    setPendingCode('');
    setPendingName('');
    setPendingScenarioCode(undefined);
    setAppliedId('');
    setAppliedCode('');
    setAppliedName('');
    setAppliedScenarioCode(undefined);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ businessCode: 1001 });
    setModalOpen(true);
  };

  const handleEdit = (item: PricingFeeItemData) => {
    setEditingItem(item);
    form.setFieldsValue({
      businessFeeCode: item.businessFeeCode,
      businessFeeName: item.businessFeeName,
      businessScenarioCode: item.businessScenarioCode,
      businessCode: 1001,
      description: item.description,
    });
    setModalOpen(true);
  };

  const handleScenarioChange = (value: number) => {
    form.setFieldsValue({ businessScenarioCode: value });
  };

  const handleModalOk = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    try {
      if (editingItem) {
        await updateFeeItem({
          variables: {
            input: {
              id: Number(editingItem.id),
              businessFeeName: values.businessFeeName,
              businessScenarioCode: values.businessScenarioCode,
              businessCode: values.businessCode,
              description: values.description ?? null,
            },
          },
        });
        message.success('更新成功');
      } else {
        await createFeeItem({
          variables: {
            input: {
              businessFeeCode: values.businessFeeCode,
              businessFeeName: values.businessFeeName,
              businessScenarioCode: values.businessScenarioCode,
              businessCode: values.businessCode,
              description: values.description ?? null,
            },
          },
        });
        message.success('创建成功');
      }
      setModalOpen(false);
      await refetch();
    } catch (error: any) {
      const errMsg = error?.graphQLErrors?.[0]?.message ?? error?.message ?? (editingItem ? '更新失败' : '创建失败');
      message.error(errMsg);
    }
  };

  const columns: ColumnsType<PricingFeeItemData> = [
    { title: 'ID', dataIndex: 'id', width: 115 },
    { title: '费用项编码', dataIndex: 'businessFeeCode', width: 130 },
    { title: '费用项名称', dataIndex: 'businessFeeName', width: 150 },
    {
      title: '业务场景',
      dataIndex: 'businessScenarioCode',
      width: 160,
      render: code => scenarioMap.get(code) ?? code,
    },
    { title: '业务编码', dataIndex: 'businessCode', width: 130 },
    {
      title: '描述',
      dataIndex: 'description',
      render: v => v ?? '-',
    },
    {
      title: '操作',
      width: 80,
      render: (_, item) =>
        hasResource([ResourceCode.menu.SETTLEMENT_FEE_ITEM_MANAGER]) ? (
          <Button type="link" size="small" onClick={() => handleEdit(item)}>
            编辑
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      title="计费费用项管理"
      extra={
        hasResource([ResourceCode.menu.SETTLEMENT_FEE_ITEM_MANAGER]) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            新增费用项
          </Button>
        )
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="ID"
          allowClear
          style={{ width: 180 }}
          value={pendingId}
          onChange={e => setPendingId(e.target.value)}
        />
        <Input
          placeholder="费用项编码"
          allowClear
          style={{ width: 140 }}
          value={pendingCode}
          onChange={e => setPendingCode(e.target.value)}
        />
        <Input
          placeholder="费用项名称"
          allowClear
          style={{ width: 160 }}
          value={pendingName}
          onChange={e => setPendingName(e.target.value)}
        />
        <Select
          placeholder="按业务场景筛选"
          allowClear
          style={{ width: 200 }}
          value={pendingScenarioCode}
          onChange={setPendingScenarioCode}
          options={scenarioOptions}
        />
        <Button type="primary" onClick={handleSearch}>查询</Button>
        <Button onClick={handleReset}>重置</Button>
      </Space>

      <Table
        rowKey="id"
        dataSource={pageData?.content ?? []}
        columns={columns}
        loading={loading}
        scroll={{ x: 900 }}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: pageData?.totalElements ?? 0,
          onChange: page => setCurrentPage(page),
          showSizeChanger: false,
          showTotal: total => `共 ${total} 条`,
        }}
      />

      <Modal
        title={editingItem ? '编辑计费费用项' : '新增计费费用项'}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={creating || updating}
        okText="确认"
        cancelText="取消"
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editingItem && (
            <Form.Item
              name="businessFeeCode"
              label="费用项编码"
              rules={[{ required: true, message: '请输入费用项编码' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="请输入编码（数字）"
                precision={0}
              />
            </Form.Item>
          )}
          <Form.Item
            name="businessFeeName"
            label="费用项名称"
            rules={[{ required: true, message: '请输入费用项名称' }]}
          >
            <Input placeholder="请输入费用项名称" />
          </Form.Item>
          <Form.Item
            name="businessScenarioCode"
            label="业务场景"
            rules={[{ required: true, message: '请选择业务场景' }]}
          >
            <Select
              placeholder="请选择业务场景"
              options={scenarioOptions}
              onChange={handleScenarioChange}
              disabled = {editingItem ? true : false}
            />
          </Form.Item>
          <Form.Item name="businessCode" label="业务编码">
            <InputNumber
              style={{ width: '100%' }}
              placeholder="选取业务场景后自动填充"
              precision={0}
              disabled
            />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input placeholder="请输入描述（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PricingFeeItemTab;
